import { useEffect, useRef } from "react";
import type { FieldValues, FormState, UseFormReset, UseFormTrigger } from "react-hook-form";
import { useUnsavedChangesGuardSplitContext } from "./context";

export interface IUseUnsavedChangesGuardFormOptions<T extends FieldValues> {
  formState: FormState<T>;
  trigger: UseFormTrigger<T>;
  onSubmit: () => Promise<void> | void;
  reset?: UseFormReset<T>;
}

export const useUnsavedChangesGuardForm = <T extends FieldValues>({
  formState,
  trigger,
  onSubmit,
  reset,
}: IUseUnsavedChangesGuardFormOptions<T>) => {
  const setIsDirty = useUnsavedChangesGuardSplitContext("setIsDirty");
  const setValidateBeforeLeave = useUnsavedChangesGuardSplitContext("setValidateBeforeLeave");
  const setOnReset = useUnsavedChangesGuardSplitContext("setOnReset");
  const setOnSubmit = useUnsavedChangesGuardSplitContext("setOnSubmit");

  const onSubmitRef = useRef(onSubmit);
  const triggerRef = useRef(trigger);
  const resetRef = useRef(reset);

  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  useEffect(() => {
    triggerRef.current = trigger;
  }, [trigger]);

  useEffect(() => {
    resetRef.current = reset;
  }, [reset]);

  useEffect(() => {
    setIsDirty(formState.isDirty);
  }, [formState.isDirty, setIsDirty]);

  useEffect(() => {
    setOnSubmit(async () => {
      await onSubmitRef.current();
    });
    setValidateBeforeLeave(() => triggerRef.current());
    setOnReset(() => resetRef.current?.());

    return () => {
      setIsDirty(false);
      setOnSubmit(null);
      setOnReset(null);
      setValidateBeforeLeave(null);
    };
  }, [setOnSubmit, setValidateBeforeLeave, setOnReset, setIsDirty]);
};
